import React from 'react';
import { CheckCircle2, Clock, Send, XCircle } from 'lucide-react';
import type { SupplierQuote, TenderSupplierRequest } from '../types';

const dateFmt = new Intl.DateTimeFormat('de-DE');

interface Props {
  ausschreibungId: string;
  quotes: SupplierQuote[];
  /** LV vorhanden und Recht zum Bearbeiten: Button „Großhändler anfragen“ zeigen. */
  canRequest: boolean;
  onRequest: () => void;
}

/** Akte: bei welchen Großhändlern angefragt wurde und ob deren Angebot schon da ist. */
export const TenderSupplierRequestsSummary: React.FC<Props> = ({ ausschreibungId, quotes, canRequest, onRequest }) => {
  const [requests, setRequests] = React.useState<TenderSupplierRequest[] | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetch(`/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}/supplier-requests`, { credentials: 'include' })
      .then(async (r) => (r.ok ? ((await r.json()) as TenderSupplierRequest[]) : []))
      .then((rows) => {
        if (!cancelled) setRequests(rows);
      })
      .catch(() => {
        if (!cancelled) setRequests([]);
      });
    return () => {
      cancelled = true;
    };
  }, [ausschreibungId]);

  const quotedOrgIds = React.useMemo(
    () => new Set(quotes.map((q) => q.supplier_organization_id).filter((id): id is string => Boolean(id))),
    [quotes],
  );

  if (requests === null) return null;
  if (requests.length === 0 && !canRequest) return null;

  return (
    <section className="rounded-lg border border-app-border bg-app-surface shadow-sm px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-app-text truncate">
          Preisanfragen
          <span className="ml-1 text-sm font-normal text-app-text-subtle">· {requests.length}</span>
        </h2>
        {canRequest && (
          <button
            type="button"
            onClick={onRequest}
            className="inline-flex items-center gap-1.5 rounded-md border border-app-border px-3 py-1.5 text-sm font-medium text-app-text hover:bg-app-hover"
          >
            <Send className="h-3.5 w-3.5" />
            Großhändler anfragen
          </button>
        )}
      </div>
      {requests.length === 0 ? (
        <p className="mt-1 text-sm text-app-text-subtle">Noch keine Großhändler angefragt.</p>
      ) : (
        <ul className="mt-2 flex flex-wrap gap-2">
          {requests.map((r) => {
            const quoted = r.organization_id != null && quotedOrgIds.has(r.organization_id);
            const icon =
              r.status === 'failed' ? (
                <XCircle className="h-3.5 w-3.5 text-red-600" />
              ) : quoted ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              ) : (
                <Clock className="h-3.5 w-3.5 text-app-text-subtle" />
              );
            const when = r.sent_at ? dateFmt.format(new Date(r.sent_at)) : null;
            const label =
              r.status === 'failed'
                ? `nicht gesendet: ${r.error ?? ''}`
                : quoted
                  ? 'Angebot liegt vor'
                  : r.status === 'pending'
                    ? 'wird gesendet'
                    : `angefragt${when ? ` am ${when}` : ''}, Angebot offen`;
            return (
              <li
                key={r.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-app-border px-2.5 py-1 text-sm"
                title={`${r.email} · ${label}`}
              >
                {icon}
                <span className="font-medium text-app-text">{r.supplier_name || r.email}</span>
                <span className="text-app-text-subtle">{quoted ? 'Angebot da' : when ?? ''}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
